import React from 'react';

import { Box, Button, Typography } from '@mui/material';

import { Field, Form, FormSpy } from '@hrlogics/react-components/form';
import { SelectFieldOption } from '@hrlogics/react-helper-types';

import { CompanyType, QueryMode } from 'src/api';
import { AutocompleteField, CheckboxField, SelectField } from 'src/components/finalForm';
import { useEmployerById } from 'src/hooks/employer';
import { usePeosAutocomplete } from 'src/hooks/peo';
import { usePeoPayrollProvidersAutocomplete } from 'src/hooks/peoPayrollProvider';

import { FormField } from './constants';
import { FormValues } from './types';
import { buildUpdateFnInput } from './utils/utils';
import { formValidationSchema } from './utils/validation';

export type EmployerControlledByConfigurationModalFormProps = {
  onCloseModal: () => void;
  switchClosability: (closability: boolean) => void;
  employerId: string;
};

export const EmployerControlledByConfigurationModalForm: React.FC<
  EmployerControlledByConfigurationModalFormProps
> = ({ onCloseModal, switchClosability, employerId }) => {
  const { employer, updateEmployer } = useEmployerById(employerId);

  const peosAutocompleteProps = usePeosAutocomplete({
    queryFilter: searchText => ({
      filter: { displayName: { contains: searchText, mode: QueryMode.Insensitive } },
    }),
  });
  const peoPpAutocompleteProps = usePeoPayrollProvidersAutocomplete({
    queryFilter: searchText => ({
      filter: { displayName: { contains: searchText, mode: QueryMode.Insensitive } },
    }),
  });

  const initialValues: Partial<FormValues> = {
    controlledBy: employer?.controlledBy,
    peo: employer?.controlledBy === CompanyType.Peo ? employer?.peo : null,
    peoPayrollProvider:
      employer?.controlledBy === CompanyType.PeoPayrollProvider
        ? employer?.peoPayrollProvider
        : null,
    keepOwnRevenueShare: employer?.keepOwnRevenueShare ?? false,
  };

  const handleSubmit = React.useCallback(
    async (formValues: FormValues) => {
      if (!employer) return;

      switchClosability(false);

      try {
        await updateEmployer({
          data: buildUpdateFnInput(formValues, employer),
        });

        switchClosability(true);
        onCloseModal();
      } catch (error) {
        console.error(error);
        switchClosability(true);
      }
    },
    [switchClosability, onCloseModal, updateEmployer, employer],
  );

  return (
    <Form<FormValues>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      subscription={{ submitting: true, valid: true }}
      validationSchema={formValidationSchema}
    >
      {({ submitting, valid }) => (
        <React.Fragment>
          <Typography variant="body1">
            This setting defines whether the employer operates independently or as part of a PEO /
            PEO Payroll Provider structure. If assigned to a parent organization, the employer
            becomes a sub-organization. The parent organization will have visibility into this
            employer and its data. Under a parent organization, the employer's own revenue share is
            paid only when "Keep this employer's own revenue share" is checked. The parent's revenue
            share is paid either way.
          </Typography>

          <Box mt={2.5}>
            <Field
              name={FormField.controlledBy}
              component={SelectField}
              label="Hierarchy Placement"
              options={CONTROLLED_BY_OPTIONS}
              disabled={submitting}
              fullWidth
              required
            />
          </Box>

          <FormSpy<FormValues> subscription={{ values: true }}>
            {({ values }) => (
              <React.Fragment>
                <React.Activity
                  mode={values.controlledBy === CompanyType.Peo ? 'visible' : 'hidden'}
                >
                  <Box mt={2}>
                    <Field
                      name={FormField.peo}
                      component={AutocompleteField}
                      label="Connected PEO"
                      disabled={submitting}
                      required={values.controlledBy === CompanyType.Peo}
                      fullWidth
                      {...peosAutocompleteProps}
                    />
                  </Box>
                </React.Activity>

                <React.Activity
                  mode={
                    values.controlledBy === CompanyType.PeoPayrollProvider ? 'visible' : 'hidden'
                  }
                >
                  <Box mt={2}>
                    <Field
                      name={FormField.peoPayrollProvider}
                      component={AutocompleteField}
                      label="Connected PEO Payroll Provider"
                      disabled={submitting}
                      required={values.controlledBy === CompanyType.PeoPayrollProvider}
                      fullWidth
                      {...peoPpAutocompleteProps}
                    />
                  </Box>
                </React.Activity>

                <React.Activity
                  mode={values.controlledBy !== CompanyType.Employer ? 'visible' : 'hidden'}
                >
                  <Box mt={2}>
                    <Field
                      name={FormField.keepOwnRevenueShare}
                      component={CheckboxField}
                      label="Keep this employer's own revenue share"
                      disabled={submitting}
                    />
                  </Box>
                </React.Activity>
              </React.Fragment>
            )}
          </FormSpy>

          <Box display="flex" justifyContent="center" mt={4} gap={2}>
            <Button
              type="button"
              variant="outlined"
              color="error"
              loading={submitting}
              disabled={submitting}
              onClick={onCloseModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              loading={submitting}
              disabled={submitting || !valid}
            >
              Apply
            </Button>
          </Box>
        </React.Fragment>
      )}
    </Form>
  );
};

const CONTROLLED_BY_OPTIONS = [
  { label: 'Standalone Employer', value: CompanyType.Employer },
  { label: 'Employer under PEO', value: CompanyType.Peo },
  { label: 'Employer under PEO Payroll Provider', value: CompanyType.PeoPayrollProvider },
] as const satisfies SelectFieldOption<CompanyType>[];
